import { useMutation, useQueryClient } from 'react-query';

import queries from '../queries';

const useChangeOwner = (callbacks = {}) => {
  return useMutation(({ oldUserId, newUserId }) => {
    return queries.leaks.transfer.query(oldUserId, newUserId);
  }, callbacks);
};

const useMoveLeaks = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ leakIds, toUserId }) => {
      return queries.leaks.move.query(leakIds, toUserId);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        queryClient.invalidateQueries([queries.leaks.getLeak.key, variables.id]);
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        queryClient.invalidateQueries([queries.assignments.list.key]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useBulkCompanyEdit = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ leakIds, targetCompanyId }) => {
      return queries.leaks.bulkCompanyEdit.query(leakIds, targetCompanyId);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        // Bulk transfer recomputes loss/energy. RESET detail caches (not invalidate): detail is
        // stale-while-revalidate + MassFlow seeds once, so clearing them forces a fresh fetch.
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.resetQueries([queries.leaks.getLeak.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useUpdateLeak = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id: leakId, ...model }) => {
      return queries.leaks.setLeak.query(leakId, 'id', model);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        queryClient.invalidateQueries([queries.leaks.getLeak.key, variables.id]);
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        queryClient.invalidateQueries([queries.leaks.history.key]);
        queryClient.invalidateQueries([queries.assignments.listByRecordId.key, variables.id]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useBulkDelete = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ids, filters }) => {
      if (ids) {
        return queries.leaks.bulkDeleteById.query(ids);
      }
      return queries.leaks.bulkDeleteByFilters.query(filters);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        queryClient.invalidateQueries([queries.leaks.getLeak.key, variables.id]);
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useBulkEdit = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ids, documentId, model }) => {
      if (ids) {
        return queries.leaks.bulkEditById.query(ids, model);
      }
      return queries.leaks.bulkEditByDocumentId.query(documentId, model);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        // RESET (not invalidate) detail caches: detail serves stale-while-revalidate and MassFlow
        // seeds its display once, so clearing them forces a fresh loss/kWh on next open.
        queryClient.resetQueries([queries.leaks.getLeak.key]);
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

// System-parameters bulk-edit mutation. Same invalidations as useBulkEdit so the
// leak list reflects the recomputed gasCost / lossPerYear for affected leaks.
const useBulkSystemParametersEdit = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ids, model, expected }) => queries.leaks.bulkSystemParametersEditById.query(ids, model, expected),
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        // RESET (not invalidate) detail caches: detail serves stale-while-revalidate and MassFlow
        // seeds its display once, so clearing them forces a fresh loss/kWh on next open.
        queryClient.resetQueries([queries.leaks.getLeak.key]);
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

// Single-record system-parameters apply. Same detail-cache reset as the bulk variant
// so the record view refetches the recomputed gasCostSummary / lossPerYear.
const useUpdateSystemParameters = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ documentId, model, expected }) => queries.leaks.updateSystemParametersById.query(documentId, model, expected),
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        queryClient.resetQueries([queries.leaks.getLeak.key]);
        queryClient.invalidateQueries([queries.leaks.list.key]);
        queryClient.invalidateQueries([queries.leaks.history.key]);
        queryClient.invalidateQueries([queries.filters.leaks.key]);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

export default {
  useChangeOwner,
  useMoveLeaks,
  useBulkCompanyEdit,
  useUpdateLeak,
  useBulkDelete,
  useBulkEdit,
  useBulkSystemParametersEdit,
  useUpdateSystemParameters,
};
